import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { ArrowLeft } from "lucide-react";
import { useGetResourceTypeList, useGetResourceSubtypeList, useGetUtilityServices, useChoices, useMeterManufacturers, useMeterModels, useMeterReasonCodesList, useMeterReadingStatusList } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useConfigMapJson } from "@shared/api/hooks";
import { useAddResource, useSearchSubmeters, useMeterDetail, useGenerateMeterNumber, useEditResource, useGetMasterMeters, useServiceOrdersJobCard } from "../../hook";
import { convertToSnakeCase } from "@shared/utils/caseConverter";
import {
  useMeterReadingFormatDetail,
  useReadingStatus,
} from "@features/mx/settings/hooks";

export default function AddInstalled() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  const { refetch: fetchGeneratedId, isFetching: isGenerating } = useGenerateMeterNumber(remoteUtilityId || "", false);
  const { mutate: addResourceMutate } = useAddResource();
  const { mutate: editResourceMutate } = useEditResource();
  const [watchResourceType, setWatchResourceType] = React.useState<string>("");
  const [watchSubType, setWatchSubType] = React.useState<string>("");
  const [watchLinkedServiceOrder, setWatchLinkedServiceOrder] = React.useState<string>("");

  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType ? [watchResourceType] : undefined
  );
  const subtypesList = subtypesResponse?.result || [];

  const selectedSubTypeObj = React.useMemo(() => {
    return subtypesList.find((s: any) => s.code === watchSubType);
  }, [subtypesList, watchSubType]);

  const formatId = selectedSubTypeObj?.readFormat;

  // Fetch schema dynamically if formatId is provided and captureReads is enabled
  const selectedTypeObj = React.useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results) ? results.find((item: any) => item.code === watchResourceType) : null;
  }, [resourceTypeResponse, watchResourceType]);

  const captureReadsEnabled = selectedTypeObj?.capabilities?.captureReads === 1;

  const { data: formatDetail } = useMeterReadingFormatDetail(
    (captureReadsEnabled && formatId) ? String(formatId) : "",
    remoteUtilityId || "",
  );

  // Fetch dropdown options for status and reason codes
  const { data: statusList } = useMeterReadingStatusList(remoteUtilityId);
  const { data: serviceOrdersJobCard } = useServiceOrdersJobCard(remoteUtilityId);

  const jobCardOptions = React.useMemo(() => {
    if (!serviceOrdersJobCard || !Array.isArray(serviceOrdersJobCard))
      return [];
    return serviceOrdersJobCard.map((jobCard: any) => ({
      value: String(jobCard.jobCardId),
      label: jobCard.soName,
    }));
  }, [serviceOrdersJobCard]);
  
  const [watchConditionStatus, setWatchConditionStatus] = React.useState<string>("");
  const { data: reasonCodes } = useMeterReasonCodesList(
    remoteUtilityId || "", 
    undefined, 
    undefined, 
    undefined, 
    watchConditionStatus
  );

  const conditionStatusOptions = React.useMemo(() => {
    const raw = statusList?.result || [];
    if (raw.length > 0) {
      return raw.map((item: any) => ({
        label: item.name,
        value: item.id,
      }));
    }
    return [];
  }, [statusList]);

  const conditionReasonOptions = React.useMemo(() => {
    const raw = reasonCodes?.result || [];
    if (raw.length > 0) {
      return raw.map((item: any) => ({
        label: item.reason,
        value: String(item.id),
      }));
    }
    return [];
  }, [reasonCodes]);


  const resourceTypeOptions = React.useMemo(() => {
    const results = resourceTypeResponse?.result || resourceTypeResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.code,
      }))
      : [];
  }, [resourceTypeResponse]);

  const { data: utilityServicesData } = useGetUtilityServices(remoteUtilityId);

  const utilityServiceOptions = React.useMemo(() => {
    return Array.isArray(utilityServicesData)
      ? utilityServicesData
        .filter((item: any) => item.isActive)
        .map((item: any) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [utilityServicesData]);

  const [watchCategory, setWatchCategory] = React.useState<string>("");

  const { data: categoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "category"
  );

  const { data: subCategoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "sub_category",
    watchCategory || undefined
  );

  const categoryOptions = React.useMemo(() => {
    return Array.isArray(categoryConfig)
      ? categoryConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [categoryConfig]);

  const subCategoryOptions = React.useMemo(() => {
    return Array.isArray(subCategoryConfig)
      ? subCategoryConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [subCategoryConfig]);

  const [watchPremise, setWatchPremise] = React.useState<string>("");

  const { data: premiseConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "premise"
  );

  const { data: unitConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "unit",
    watchPremise || undefined
  );

  const premiseOptions = React.useMemo(() => {
    return Array.isArray(premiseConfig)
      ? premiseConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [premiseConfig]);

  const unitOptions = React.useMemo(() => {
    return Array.isArray(unitConfig)
      ? unitConfig.map((item: any) => ({
        label: item.name || item,
        value: item.code || item,
      }))
      : [];
  }, [unitConfig]);

  const { data: subMeters } = useSearchSubmeters(remoteUtilityId);

  const subMeterOptions = React.useMemo(() => {
    const results = subMeters?.results || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: `${item.meterNumber} (${item.deviceNo})`,
        value: item.id,
      }))
      : [];
  }, [subMeters]);

  const { data: classificationChoices } = useChoices({
    choice: "MeterOwnership",
    remoteUtilityId,
  });

  const classificationOptions = React.useMemo(() => {
    return Array.isArray(classificationChoices)
      ? classificationChoices.map((item: any) => ({
        label: item.value,
        value: item.key,
      }))
      : [];
  }, [classificationChoices]);

  const { data: masterMeters } = useGetMasterMeters(remoteUtilityId);

  const masterResourceOptions = React.useMemo(() => {
    return Array.isArray(masterMeters)
      ? masterMeters.map((item: any) => ({
        label: `${item.meterNumber} (${item.deviceNo})`,
        value: item.id,
      }))
      : [];
  }, [masterMeters]);

  const { data: manufacturerResponse } = useMeterManufacturers({
    remote_utility_id: remoteUtilityId || "",
    is_pagination_required: false,
  });

  const manufacturerOptions = React.useMemo(() => {
    const results = manufacturerResponse?.results;
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.name,
        value: item.id,
      }))
      : [];
  }, [manufacturerResponse]);

  const [watchManufacturer, setWatchManufacturer] = React.useState<string>("");

  const { data: modelsResponse } = useMeterModels(
    remoteUtilityId,
    watchManufacturer || undefined
  );

  const modelOptions = React.useMemo(() => {
    const results = modelsResponse?.results || modelsResponse || [];
    return Array.isArray(results)
      ? results.map((item: any) => ({
        label: item.meterModel,
        value: item.id,
      }))
      : [];
  }, [modelsResponse]);

  const isEditMode = !!id;
  const { data: apiResource } = useMeterDetail(id || "", remoteUtilityId);
  const existingResource = isEditMode ? apiResource : null;

  const initialValues = {
    resourceId: existingResource?.id || "",
    serialNo: existingResource?.serialNo || "",
    utilityService: existingResource?.utilityService || "",
    type: existingResource?.type || "",
    subType: existingResource?.subType || "",
    classification: existingResource?.classification || "",
    category: existingResource?.category || "",
    subCategory: existingResource?.subCategory || "",
    manufacturer: existingResource?.manufacturer || "",
    model: existingResource?.model || "",
    premise: existingResource?.premise || "",
    unit: existingResource?.unit || "",
    latitude: existingResource?.latitude || "",
    longitude: existingResource?.longitude || "",
    physicalStatus: existingResource?.physicalStatus || "Installed",
    installationDate: existingResource?.installedOn || "",
    serviceOrderRaised: "No",
    linkedServiceOrder: "",
    parent_meter: existingResource?.parentMeter || "",
  };

  const selectedSoCard = React.useMemo(() => {
    return serviceOrdersJobCard?.find((jc: any) => String(jc.jobCardId) === String(watchLinkedServiceOrder)) || null;
  }, [serviceOrdersJobCard, watchLinkedServiceOrder]);

  console.log(selectedSoCard, watchLinkedServiceOrder, serviceOrdersJobCard)
  const baseFormFields: FormField[] = React.useMemo(() => [
    // --- Section 1: Resource Information ---
    {
      name: "resourceId",
      type: "text",
      label: "Resource ID",
      placeholder: "e.g. RES-0001",
      required: true,
      helperText: "Unique identifier — format: RES-XXXX",
      className: "col-span-4",
      group: "Resource Information",
      groupHelperText: "Select the resource type and sub type to configure this resource.",
      groupBorderNeeded: false,
    },
    {
      name: "generateButton",
      type: "button",
      label: "Generate ID",
      buttonLabel: isGenerating ? "Generating..." : "Generate",
      disabled: isGenerating || !remoteUtilityId,
      className: "col-span-1 pr-2 mt-2",
      helperText: "Click to generate resource id.",
      group: "Resource Information",
      groupBorderNeeded: false,
      onClick: async () => {
        try {
          const res = await fetchGeneratedId();
          if (res.data && res.data.meterNumber) {
            form.setValue("resourceId", res.data.meterNumber, { shouldValidate: true });
            toast({ title: "Generated", description: `Resource ID generated: ${res.data.meterNumber}` });
          }
        } catch (error) {
          toast({ title: "Error", description: "Failed to generate Resource ID", variant: "destructive" });
        }
      },
    },
    {
      name: "serialNo",
      type: "text",
      label: "Serial Number",
      placeholder: "e.g. SN-HTX-00101",
      helperText: "Unique serial number printed on the physical device.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
    },
    {
      name: "utilityService",
      type: "select",
      label: "Utility Service",
      placeholder: "Select utility service",
      required: true,
      helperText: "Select the utility service type.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: utilityServiceOptions,
    },
    {
      name: "type",
      type: "select",
      label: "Resource Type",
      placeholder: "Select resource type",
      required: true,
      helperText: "Select the category of resource being added.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: resourceTypeOptions,
    },
    {
      name: "subType",
      type: "select",
      label: "Resource Sub Type",
      placeholder: "select a type first",
      required: true,
      helperText: "Sub type determines which fields are collected.",
      disabled: (formValues) => !formValues.type,
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: subtypesList.map((item: any) => ({
        label: item.name,
        value: item.code,
      })),
    },
    {
      name: "classification",
      type: "select",
      label: "Resource Classification",
      placeholder: "Select classification",
      required: true,
      helperText: "Define the role of this resource within the hierarchy.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: classificationOptions,
    },
    {
      name: "parent_meter",
      type: "select",
      label: "Master Resource",
      placeholder: "Select master resource",
      required: true,
      helperText: "Select the master resource this sub resource belongs to.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: masterResourceOptions,
      showWhen: (formValues) => formValues.classification === 2 || formValues.classification === "2",
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      placeholder: "Select category",
      helperText: "Broad category this resource belongs to.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: categoryOptions,
    },
    {
      name: "subCategory",
      type: "select",
      label: "Sub-Category",
      placeholder: "select a category first",
      helperText: "Specific sub-category within the selected category.",
      disabled: (formValues) => !formValues.category,
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: subCategoryOptions,
    },
    {
      name: "manufacturer",
      type: "select",
      label: "Manufacturer",
      placeholder: "Select manufacturer",
      helperText: "Company that manufactured this resource.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: manufacturerOptions,
    },
    {
      name: "model",
      type: "select",
      label: "Model",
      placeholder: "select a manufacturer first",
      helperText: "Specific model of the resource.",
      disabled: (formValues) => !formValues.manufacturer,
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: modelOptions,
    },
    {
      name: "linkedSubMeters",
      type: "multi-select",
      label: "Link Sub Resources",
      placeholder: "Search sub resources...",
      helperText: "Search and select existing sub resources to attach to this master.",
      className: "col-span-5",
      group: "Resource Information",
      groupBorderNeeded: false,
      options: subMeterOptions,
      showWhen: (formValues) => formValues.classification === 0 || formValues.classification === "0",
    },
    // --- Section 2: Location Details ---
    {
      name: "locationDetailsLink",
      type: "custom",
      className: "col-span-10",
      group: "Location Details",
      groupHelperText: "Define where the resource is installed.",
      groupBorderNeeded: false,
      customRender: () => (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-4 py-3 rounded-lg flex items-center justify-between w-full mt-1 mb-3">
          <span>Can't find the premise or unit? Want to add a new one?</span>
          <a
            href="/utility/utility-setup/service-area"
            className="font-bold underline hover:text-amber-950 transition-colors"
          >
            Go to Service Areas
          </a>
        </div>
      ),
    },
    {
      name: "premise",
      type: "select",
      label: "Premise",
      placeholder: "Select premise",
      required: true,
      helperText: "Select the premise location.",
      className: "col-span-5",
      group: "Location Details",
      groupBorderNeeded: false,
      options: premiseOptions,
    },
    {
      name: "unit",
      type: "select",
      label: "Unit",
      placeholder: "select a premise first",
      disabled: (formValues) => !formValues.premise,
      helperText: "Select the unit at the selected premise.",
      className: "col-span-5",
      group: "Location Details",
      groupBorderNeeded: false,
      options: unitOptions,
    },
    {
      name: "latitude",
      type: "text",
      label: "Latitude",
      placeholder: "e.g. 34.0522° N",
      helperText: "Enter the latitude coordinates.",
      className: "col-span-5",
      group: "Location Details",
      groupBorderNeeded: false,
    },
    {
      name: "longitude",
      type: "text",
      label: "Longitude",
      placeholder: "e.g. 118.2437° W",
      helperText: "Enter the longitude coordinates.",
      className: "col-span-5",
      group: "Location Details",
      groupBorderNeeded: false,
    },

    // --- Section 3: Installation Details ---
    {
      name: "physicalStatus",
      type: "select",
      label: "Physical Status",
      placeholder: "Installed",
      required: true,
      disabled: true,
      helperText: "Current physical status of this resource.",
      className: "col-span-5",
      group: "Installation Details",
      groupHelperText: "Installation setup and service order linkage.",
      groupBorderNeeded: false,
      options: [{ label: "Installed", value: "Installed" }],
    },
    {
      name: "installationDate",
      type: "datepicker",
      label: "Installation Date",
      placeholder: "dd/mm/yyyy",
      required: true,
      helperText: "Date when the resource was installed.",
      className: "col-span-5",
      group: "Installation Details",
      groupBorderNeeded: false,
    },
    {
      name: "serviceOrderRaised",
      type: "radio",
      label: "Is a Service Order Raised?",
      helperText: "Select whether a service order is raised.",
      className: "col-span-5",
      group: "Installation Details",
      groupBorderNeeded: false,
      options: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
    },
    {
      name: "linkedServiceOrder",
      type: "select",
      options: jobCardOptions,
      label: "Search Service Order",
      placeholder: "Search service order",
      group: "Installation Details",
      groupHelperText: "Installation setup, site coordinates, and service order linkage.",
      groupColorNeeded: false,
      showWhen: (formValues) => formValues.serviceOrderRaised === "Yes",
      className: "col-span-5",
      helperText: "Search by service order ID, name, or assigned user",
    },
    {
      name: "so_details_card",
      type: "custom",
      dependsOn: "linkedServiceOrder",
      label: "Service Order Details",
      showWhen: (formValues: any) =>
        !!formValues.linkedServiceOrder && formValues.serviceOrderRaised === "Yes",
      group: "Installation Details",
      groupColorNeeded: false,
      className: "col-span-10",
      customRender: () => (
        <div className="p-4 border rounded-lg bg-slate-50 flex flex-col gap-2 w-full mt-2">
          <div className="grid grid-cols-4 gap-4 w-full">
            <div>
              <p className="text-xs text-slate-500 mb-1">SO ID</p>
              <p className="font-medium text-sm">{selectedSoCard?.soId ?? "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">SO Name</p>
              <p className="font-medium text-sm">{selectedSoCard?.soName ?? "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Completed On</p>
              <p className="font-medium text-sm">{selectedSoCard?.completedOn ?? "N/A"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Assigned To</p>
              <p className="font-medium text-sm">{selectedSoCard?.assignedTo ?? "N/A"}</p>
            </div>
          </div>
        </div>
      ),
    },

  ], [resourceTypeResponse, resourceTypeOptions, subtypesList, utilityServiceOptions, categoryOptions, subCategoryOptions, classificationOptions, manufacturerOptions, modelOptions, premiseOptions, unitOptions, subMeterOptions, masterResourceOptions, jobCardOptions, selectedSoCard]);

  // Dynamically resolve custom fields based on selected subType config
  const dynamicFields: FormField[] = React.useMemo(() => {
    if (!watchSubType) return [];

    // Search for subtype matching current value
    const selected = subtypesList.find((s: any) => s.code === watchSubType);

    const target = selected;

    if (!target) return [];

    const apiCustomFields = target.customFields || [];

    console.log('custom fields', apiCustomFields)

    // If customFields array exists
    if (Array.isArray(apiCustomFields) && apiCustomFields.length > 0) {
      return apiCustomFields.map((cf: any) => {
        const labelStr = cf.label || cf.fieldLabel || cf.field_label || "";
        const rawType = (cf.type || "text").toLowerCase();
        let fieldType = rawType;
        if (rawType === "radio" || rawType === "yes/no") {
          fieldType = "radio";
        } else if (rawType === "switch") {
          fieldType = "switch";
        } else if (rawType === "dropdown" || rawType === "select") {
          fieldType = "select";
        }

        return {
          name: `${labelStr.replace(/\s+/g, "_").toLowerCase()}`,
          type: fieldType,
          label: labelStr,
          placeholder: cf.placeholder || `Enter ${labelStr.toLowerCase()}`,
          required: cf.required !== undefined
            ? cf.required
            : (cf.isRequired !== undefined
              ? cf.isRequired
              : (cf.is_required !== undefined ? cf.is_required : false)),
          className: "col-span-5",
          group: "Specifications",
          groupHelperText: "Configure specification details for the selected sub type.",
          groupBorderNeeded: false,
          options: Array.isArray(cf.options)
            ? cf.options.map((o: any) => typeof o === "string" ? { label: o, value: o } : o)
            : (fieldType === "radio" ? [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] : []),
        };
      });
    }

    return [];
  }, [watchSubType, subtypesList]);

  const readFormatFields = React.useMemo(() => {
    if (!captureReadsEnabled || !formatDetail) return [];

    const flatFields: FormField[] = [];
    if (formatDetail?.sections && Array.isArray(formatDetail.sections)) {
      const formatSection = formatDetail.sections.find(
        (sec: any) => sec.section === "Format Fields"
      );

      if (formatSection?.fields && Array.isArray(formatSection.fields)) {
        formatSection.fields.forEach((f: any) => {
          if (f.isActive === false) return;

          const fieldTypeUpper = (f.fieldType || "").toUpperCase();
          let type: FormField["type"] = "text";
          if (fieldTypeUpper === "DATE") {
            type = "datepicker";
          } else if (fieldTypeUpper === "NUMERIC") {
            type = "number";
          } else if (fieldTypeUpper === "DROPDOWN") {
            type = "select";
          } else if (fieldTypeUpper === "PHOTO") {
            type = "file";
          }

          const fieldNameVal = f.fieldName || f.label || "";
          const fieldKey = f.key || f.fieldName || "";

          const field: FormField = {
            name: fieldKey,
            label: fieldNameVal,
            type,
            required: f.required !== undefined ? f.required : (f.isRequired !== undefined ? f.isRequired : false),
            placeholder: `Enter ${fieldNameVal.toLowerCase()}`,
            className: "col-span-5",
            group: "Read Details",
            groupHelperText: "Details required for recording resource readings.",
            groupBorderNeeded: false,
          };

          if (fieldTypeUpper === "DROPDOWN") {
            if (fieldKey === "condition_status" || fieldKey === "conditionStatus") {
              field.options = conditionStatusOptions;
            } else if (fieldKey === "condition_reason" || fieldKey === "conditionReason") {
              field.options = conditionReasonOptions;
            } else {
              field.options = [];
            }
          }

          flatFields.push(field);
        });

        // Sort by sequenceId
        flatFields.sort((a, b) => {
          const fieldA = formatSection.fields.find((f: any) => (f.key || f.fieldName || f.field_name) === a.name);
          const fieldB = formatSection.fields.find((f: any) => (f.key || f.fieldName || f.field_name) === b.name);
          const seqA = fieldA?.sequenceId ?? fieldA?.sequenceId ?? 0;
          const seqB = fieldB?.sequenceId ?? fieldB?.sequenceId ?? 0;
          return seqA - seqB;
        });
      }
    }

    return flatFields;
  }, [formatDetail, captureReadsEnabled, conditionStatusOptions, conditionReasonOptions]);

  const formFields = React.useMemo(() => {
    return [...baseFormFields, ...readFormatFields, ...dynamicFields];
  }, [baseFormFields, readFormatFields, dynamicFields]);

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: initialValues,
  });

  const watchedResourceId = form.watch("resourceId");

  const currentResourceType = form.watch("type");
  useEffect(() => {
    setWatchResourceType(currentResourceType || "");
  }, [currentResourceType]);

  const currentCategory = form.watch("category");
  useEffect(() => {
    setWatchCategory(currentCategory || "");
  }, [currentCategory]);

  const currentLinkedServiceOrder = form.watch("linkedServiceOrder");
  useEffect(() => {
    setWatchLinkedServiceOrder(currentLinkedServiceOrder || "");
  }, [currentLinkedServiceOrder]);

  const currentManufacturer = form.watch("manufacturer");
  useEffect(() => {
    setWatchManufacturer(currentManufacturer || "");
  }, [currentManufacturer]);

  const currentPremise = form.watch("premise");
  useEffect(() => {
    setWatchPremise(currentPremise || "");
  }, [currentPremise]);

  const currentSubType = form.watch("subType");
  useEffect(() => {
    setWatchSubType(currentSubType || "");
  }, [currentSubType]);

  const currentConditionStatus = form.watch("condition_status");
  const currentConditionStatusCamel = form.watch("conditionStatus");
  useEffect(() => {
    setWatchConditionStatus(currentConditionStatus || currentConditionStatusCamel || "");
  }, [currentConditionStatus, currentConditionStatusCamel]);

  useEffect(() => {
    if (existingResource) {
      const formValues: any = {
        resourceId: existingResource.meterNumber || "",
        serialNo: existingResource.deviceNo || "",
        utilityService: existingResource.productCode || "",
        type: existingResource.resourceTypeCode || "",
        subType: existingResource.resourceSubTypeCode || "",
        classification: existingResource.ownership || "",
        category: existingResource.category || "",
        subCategory: existingResource.subCategory || "",
        manufacturer: existingResource.meterMake || "",
        model: existingResource.meterModel?.id || "",
        premise: existingResource.premise || "",
        unit: existingResource.unitCode || "",
        latitude: existingResource.lat || "",
        longitude: existingResource.long || "",
        physicalStatus: existingResource.physicalStatus || "Installed",
        installationDate: existingResource.installedOn || "",
        serviceOrderRaised: existingResource.serviceOrderId ? "Yes" : "No",
        linkedServiceOrder: existingResource.serviceOrderId ? String(existingResource.serviceOrderId) : "",
        linkedSubMeters: existingResource?.linkedSubMeters?.map((submeter: any) => submeter.id) || [],
        parent_meter: existingResource.parentMeter || "",
      };

      if (existingResource?.additionalData?.resourceCustomFields) {
        const customFields = convertToSnakeCase(existingResource.additionalData.resourceCustomFields);
        Object.keys(customFields).forEach((key) => {
          formValues[key] = customFields[key];
        });
      }

      FormService.populateForm(form, formValues, formFields);

      // Re-apply custom fields manually because FormService.populateForm drops them 
      // if dynamicFields are not yet loaded in formFields.
      if (existingResource?.additionalData?.resourceCustomFields) {
        const customFields = convertToSnakeCase(existingResource.additionalData.resourceCustomFields);
        Object.keys(customFields).forEach((key) => {
          form.setValue(key, customFields[key]);
        });
      }
    } else {
      form.reset(initialValues);
    }
  }, [id, existingResource]);

  const handleSubmit = (data: any) => {
    const dynamicPayload: any = {};
    dynamicFields.forEach((field) => {
      if (data[field.name] !== undefined) {
        dynamicPayload[field.name] = data[field.name];
      }
    });

    const payload: any = {
      meter_number: data.resourceId,
      device_no: data.serialNo,
      product_code: data.utilityService,
      category: data.category,
      sub_category: data.subCategory,
      meter_make: data.manufacturer,
      meter_model: data.model,
      premise: data.premise,
      lat: data.latitude?.toString(),
      long: data.longitude?.toString(),
      installed_on: data.installationDate,
      is_so_raised: data.serviceOrderRaised === "Yes" ? "1" : "0",
      service_order_id: data.serviceOrderRaised === "Yes" ? data.linkedServiceOrder : undefined,
      remote_utility_id: remoteUtilityId,
      physical_status: 0,
      ownership: data.classification,
      resource_type_code: data.type,
      resource_sub_type_code: data.subType,
      unit_code: data.unit,
      additional_data: { resource_custom_fields: dynamicPayload },
    };

    if (captureReadsEnabled) {
      readFormatFields.forEach((field) => {
        if (data[field.name] !== undefined && data[field.name] !== "") {
          payload[field.name] = data[field.name];
        }
      });
    }

    if (data.classification === 0 || data.classification === "0") {
      payload.linked_sub_meters = data.linkedSubMeters || [];
    }

    if (data.classification === 2 || data.classification === "2") {
      payload.parent_meter = data.parent_meter ? Number(data.parent_meter) : undefined;
    }

    if (isEditMode) {
      editResourceMutate(
        { resourceId: id, payload },
        {
          onSuccess: () => {
            navigate("/mx/manage/resources");
          },
        }
      );
    } else {
      addResourceMutate(payload, {
        onSuccess: () => {
          navigate("/mx/manage/resources");
        }
      });
    }
  };

  return (
    <div className="p-3">
      <PageHeader
        title={isEditMode ? "Edit details" : "Add New Resource"}
        subtitle={isEditMode ? "Update details of the installed resource." : "Fill in the details to add a new resource"}
        icon={ArrowLeft}
        onIconClick={() => navigate("/mx/manage/resources")}
      >
        <span className="text-xs font-bold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
          {watchedResourceId ? `${watchedResourceId}` : "New Resource"}
        </span>
      </PageHeader>

      <div className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm mt-2">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={() => navigate("/mx/manage/resources")}
          gridColumns={10}
          responsiveColumns={{ sm: 1, md: 10 }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText: isEditMode ? "Save Changes" : "Add New Resource",
            resetButtonText: "Cancel",
            classes: FIELD_CLASSES,
          }}
        />
      </div>
    </div>
  );
}
